import { TextField } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import makeStyles from '@mui/styles/makeStyles';
import React, { useState } from 'react';
import { handleUnauthorizedResponse } from '../../api/http';
import PolicyApi from '../../api/PolicyApi';
import { AuthData } from '../../context/auth';
import { ErrorData } from '../../context/ErrorProvider';
import { PolicyData, withPolicyContext } from '../../context/policy';
import {
	POLICY_ACTION_CHANGE_PAYMENT_PLAN_DIALOG_TITLE,
	POLICY_ACTION_CHANGE_PAYMENT_PLAN_DROPDOWN_BUTTON_LABEL,
} from '../../data/labelsAndMessages';
import DefaultDialog from '../DefaultDialog';
import DropdownButtonItem from '../DropdownButtonItem';
import FieldLabel from '../fields/FieldLabel';
import { successToast } from '../Toast';
import ReactSelect from './fields/ReactSelect';

interface Props {
	authData: AuthData;
	policyData: PolicyData;
	errorData: ErrorData;
}

const useStyles = makeStyles((theme) => ({
	disabledInput: {
		'& .MuiInputBase-root.Mui-disabled': {
			color: 'rgba(0, 0, 0, 0.54)',
		},
	},
	paymentScheduleControl: {
		width: '30%',
		marginTop: '16px',
		marginBottom: '8px',
		'& .MuiFormLabel-root.MuiFormLabel-root': {
			height: 'initial',
		},
		flexGrow: 1,
	},
}));

const ChangePaymentPlanTrigger = (props: Props) => {
	const classes = useStyles();
	const { policyData, authData, errorData } = props;

	const { productConfig, policy, setLoading, loadData } = policyData;

	const paymentPlanOptions = productConfig.policyConfiguration.paymentSchedules;
	const newPaymentPlans = paymentPlanOptions.filter(
		(p) => p.name !== policy.paymentScheduleName,
	);
	const newPaymentPlansArr = newPaymentPlans.map((v) => ({
		value: v.name,
		label: v.displayName,
	}));
	const currentPaymentPlanName = paymentPlanOptions.find(
		(p) => p.name === policy.paymentScheduleName,
	)?.displayName;

	const [isOpen, setIsOpen] = React.useState(false);
	const [selectedPaymentPlan, setSelectedPaymentPlan] = useState(
		newPaymentPlansArr[0],
	);
	const close = () => setIsOpen(false);
	const open = () => setIsOpen(true);

	const confirmChange = async () => {
		close();
		setLoading(true);
		try {
			const req = {
				newPaymentScheduleName: selectedPaymentPlan.value,
			};
			await PolicyApi.changePaymentPlan(
				authData.auth.authorizationToken,
				policy.locator,
				req,
			);
			successToast('Successfully changed payment schedule');
			loadData(policy.locator);
		} catch (error) {
			console.error(error);
			errorData.handleErrorResponse(error, 'Failed to change payment schedule');
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
		} finally {
			setLoading(false);
		}
	};

	const updatePaymentPlan = (option: any) => {
		setSelectedPaymentPlan(option);
	};

	const renderPaymentPlans = () => {
		return (
			<div style={{ display: 'flex' }}>
				<FormControl
					className={classes.paymentScheduleControl}
					style={{ marginRight: '10px' }}
				>
					<FieldLabel title={'Current Payment Schedule'} />
					<TextField
						id='current-payment-schedule'
						className={classes.disabledInput}
						value={currentPaymentPlanName}
						onChange={null}
						disabled
					/>
				</FormControl>
				<FormControl className={classes.paymentScheduleControl}>
					<FieldLabel required title={'New Payment Schedule'} />
					<ReactSelect
						id='new-payment-schedule-selector'
						value={selectedPaymentPlan}
						options={newPaymentPlansArr}
						onChange={updatePaymentPlan}
						disableOpenMenuOnFocus
					/>
				</FormControl>
			</div>
		);
	};

	return (
		<>
			<DropdownButtonItem
				onClick={open}
				label={POLICY_ACTION_CHANGE_PAYMENT_PLAN_DROPDOWN_BUTTON_LABEL()}
			/>
			<DefaultDialog
				actions={[
					{ text: 'Confirm', onClick: confirmChange },
					{ text: 'Close', onClick: close },
				]}
				open={isOpen}
				onClose={close}
				title={POLICY_ACTION_CHANGE_PAYMENT_PLAN_DIALOG_TITLE()}
			>
				{renderPaymentPlans()}
			</DefaultDialog>
		</>
	);
};

export default withPolicyContext<Props>(ChangePaymentPlanTrigger);
